import React, { useRef, useState, useCallback, useMemo } from 'react'
import { MATRIX_CELLS, CATEGORY_TO_INDEX } from '@range-reader/poker-core'
import { MatrixCell } from './MatrixCell'
import { EliminationPopup } from './EliminationPopup'
import { useMatrixKeyboard } from '../../hooks/useMatrixKeyboard'
import { useSessionStore } from '../../stores/sessionStore'
import type { MatrixCellStatus } from '@range-reader/shared'
import { GTO_OPEN_RANGES, GTO_POSITIONS } from '../../data/gto-ranges'

// Hand category presets for "Select by Category"
const CATEGORY_PRESETS: Record<string, string[]> = {
  'Pocket Pairs': ['AA','KK','QQ','JJ','TT','99','88','77','66','55','44','33','22'],
  'Premium Pairs': ['AA','KK','QQ','JJ'],
  'Broadway': ['AKs','AQs','AJs','ATs','KQs','KJs','KTs','QJs','QTs','JTs','AKo','AQo','AJo','ATo','KQo','KJo','KTo','QJo','QTo','JTo'],
  'Suited Connectors': ['AKs','KQs','QJs','JTs','T9s','98s','87s','76s','65s','54s','43s','32s'],
  'Suited Aces': ['AKs','AQs','AJs','ATs','A9s','A8s','A7s','A6s','A5s','A4s','A3s','A2s'],
  'Suited Kings': ['KQs','KJs','KTs','K9s','K8s','K7s','K6s','K5s','K4s','K3s','K2s'],
}

interface ComboMatrixProps {
  showActualHand?: boolean
  actualHandCategory?: string
}

interface BlockedAttempt {
  handLabel: string
  street: string
}

export function ComboMatrix({ showActualHand = false, actualHandCategory }: ComboMatrixProps) {
  const {
    matrixState,
    comboCounts,
    focusedCell,
    setFocusedCell,
    toggleCell,
    selectAll,
    clearAll,
    undoLastToggle,
    eliminationLog,
    phase,
  } = useSessionStore()

  const containerRef = useRef<HTMLDivElement>(null)
  const [blockedAttempt, setBlockedAttempt] = useState<BlockedAttempt | null>(null)
  const [gtoPosition, setGtoPosition] = useState<string>('')

  const gtoRangeSet = useMemo(() => {
    if (!gtoPosition) return new Set<number>();
    const hands = GTO_OPEN_RANGES[gtoPosition] ?? [];
    const indices = new Set<number>();
    for (const hand of hands) {
      const idx = CATEGORY_TO_INDEX.get(hand);
      if (idx !== undefined) indices.add(idx);
    }
    return indices;
  }, [gtoPosition])

  useMatrixKeyboard(containerRef)

  const handleToggle = useCallback((idx: number) => {
    const status: MatrixCellStatus = matrixState[idx]
    if (status === 'ELIMINATED') {
      // Hard locked — show popup with street it was locked on
      const entry = eliminationLog.find(e => e.handLabel === MATRIX_CELLS[idx]?.category)
      if (entry) setBlockedAttempt({ handLabel: entry.handLabel, street: entry.street })
      return
    }
    toggleCell(idx)  // HIGHLIGHTED, UNHIGHLIGHTED, or REMOVED all handled in store
  }, [matrixState, eliminationLog, toggleCell])

  const isDragging = useRef(false)
  const dragToggleMode = useRef<'on' | 'off'>('on')
  const dragVisited = useRef<Set<number>>(new Set())

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const idxStr = (e.target as HTMLElement).closest('[data-idx]')?.getAttribute('data-idx')
    if (idxStr == null) return
    const idx = parseInt(idxStr)
    isDragging.current = true
    dragVisited.current = new Set([idx])
    dragToggleMode.current = matrixState[idx] === 'HIGHLIGHTED' ? 'off' : 'on'
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    setFocusedCell(idx)
    handleToggle(idx)
  }, [matrixState, handleToggle, setFocusedCell])

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return
    const el = document.elementFromPoint(e.clientX, e.clientY)
    const idxStr = el?.closest('[data-idx]')?.getAttribute('data-idx')
    if (idxStr == null) return
    const idx = parseInt(idxStr)
    if (dragVisited.current.has(idx)) return
    dragVisited.current.add(idx)
    const current = matrixState[idx]
    if (current === 'ELIMINATED') return
    if (dragToggleMode.current === 'on' && current !== 'HIGHLIGHTED') toggleCell(idx)
    if (dragToggleMode.current === 'off' && current === 'HIGHLIGHTED') toggleCell(idx)
  }, [matrixState, toggleCell])

  const handlePointerUp = useCallback(() => {
    isDragging.current = false
    dragVisited.current.clear()
  }, [])

  const isShowdown = phase === 'SHOWDOWN'

  const handleSelectCategory = useCallback((categoryName: string) => {
    if (!categoryName || isShowdown) return
    const hands = CATEGORY_PRESETS[categoryName]
    if (!hands) return
    const next = [...matrixState]
    for (const hand of hands) {
      const idx = CATEGORY_TO_INDEX.get(hand)
      if (idx !== undefined && next[idx] !== 'ELIMINATED') {
        next[idx] = 'HIGHLIGHTED'
      }
    }
    useSessionStore.setState({ matrixState: next })
  }, [matrixState, isShowdown])
  const highlightedCount = matrixState.filter(s => s === 'HIGHLIGHTED').length
  const pairsCount = matrixState.filter((s, i) => s === 'HIGHLIGHTED' && MATRIX_CELLS[i]?.isPair).length
  const suitedCount = matrixState.filter((s, i) => s === 'HIGHLIGHTED' && MATRIX_CELLS[i]?.isSuited).length
  const offsuitCount = matrixState.filter((s, i) => s === 'HIGHLIGHTED' && MATRIX_CELLS[i]?.isOffsuit).length

  // Percentage of combos still in play, weighted by actual combinations
  // (accounts for board removal: e.g. if A♠ is on the board, AA drops from 6 to 3 combos)
  let activeCombos = 0
  let totalCombos = 0
  for (let i = 0; i < matrixState.length; i++) {
    const n = comboCounts[i] ?? 0
    totalCombos += n
    if (matrixState[i] === 'HIGHLIGHTED') activeCombos += n
  }
  const pctHands = totalCombos > 0 ? (activeCombos / totalCombos) * 100 : 0

  return (
    <div className="combo-matrix-wrapper">
      {/* Toolbar */}
      <div className="matrix-toolbar">
        <button onClick={selectAll} disabled={isShowdown} className="matrix-btn">All</button>
        <button onClick={clearAll} disabled={isShowdown} className="matrix-btn">None</button>
        <button onClick={undoLastToggle} disabled={isShowdown} className="matrix-btn">Undo</button>
        <select
          className="matrix-btn matrix-category-select"
          disabled={isShowdown}
          value=""
          onChange={e => { handleSelectCategory(e.target.value); e.target.value = '' }}
        >
          <option value="">Select...</option>
          {Object.keys(CATEGORY_PRESETS).map(name => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
        <select
          className="matrix-btn matrix-gto-select"
          value={gtoPosition}
          onChange={e => setGtoPosition(e.target.value)}
        >
          <option value="">GTO...</option>
          {GTO_POSITIONS.map(pos => (
            <option key={pos} value={pos}>{pos} ({GTO_OPEN_RANGES[pos].length})</option>
          ))}
        </select>
        <div className="matrix-stats">
          <span className="matrix-stat-pair">{pairsCount}P</span>
          <span className="matrix-stat-suited">{suitedCount}s</span>
          <span className="matrix-stat-offsuit">{offsuitCount}o</span>
          <span className="matrix-stat-total">{highlightedCount}/169</span>
          <span
            className="matrix-stat-pct"
            title={`${activeCombos} of ${totalCombos} possible combos still in range`}
          >
            {pctHands.toFixed(1)}%
          </span>
        </div>
      </div>

      {/* Legend */}
      <div className="matrix-legend">
        <span className="legend-pair">■ Pairs</span>
        <span className="legend-suited">■ Suited</span>
        <span className="legend-offsuit">■ Offsuit</span>
        {gtoPosition && <span className="legend-gto">● GTO</span>}
      </div>

      {/* Grid */}
      <div
        ref={containerRef}
        tabIndex={0}
        className="matrix-grid"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        {MATRIX_CELLS.map((cell, idx) => (
          <MatrixCell
            key={idx}
            idx={idx}
            category={cell.category}
            comboCount={comboCounts[idx] ?? 0}
            status={matrixState[idx] ?? 'HIGHLIGHTED'}
            isFocused={focusedCell === idx}
            isActualHand={showActualHand && actualHandCategory === cell.category}
            isPair={cell.isPair}
            isSuited={cell.isSuited}
            isOffsuit={cell.isOffsuit ?? false}
            onFocus={setFocusedCell}
            inGtoRange={gtoRangeSet.has(idx)}
          />
        ))}
      </div>

      {blockedAttempt && (
        <EliminationPopup
          handLabel={blockedAttempt.handLabel}
          street={blockedAttempt.street}
          onDismiss={() => setBlockedAttempt(null)}
        />
      )}
    </div>
  )
}
